import { EventInteraction, useTracking } from '@eventbrite/ads';
import { Avatar } from '@eventbrite/eds-avatar';
import { gettext } from '@eventbrite/i18n';
import React, { useContext } from 'react';
import { LISTING_HEAP_PAGE_AREA_IDS } from '../../../constants';
import { TrackingContext } from '../../../contexts';
import { useOrganizerBasicInfo } from '../../../contexts/OrganizerContext';
import { FollowButton } from '../FollowButton/FollowButton';
import { OrganizerStats, StyleTypes } from '../OrganizerStats/OrganizerStats';
import './SimplifiedOrganizerInfo.scss';
import {
    aggregateContext,
    LISTINGS_EXPIRED_INTERFACE,
    LISTINGS_LIVE_INTERFACE,
    MINIMAL_ORGANIZER_SUBINTERFACE,
} from './utils';

export interface SimplifiedOrganizerInfoProps {
    style?: StyleTypes;
    pageArea?: string;
}

export const SimplifiedOrganizerInfo: React.FunctionComponent<
    SimplifiedOrganizerInfoProps
> = ({ pageArea, style = StyleTypes.CONDENSED_FULL_WIDTH }) => {
    const { name, avatar } = useOrganizerBasicInfo();

    const context = useContext(TrackingContext);
    const getTrackingContext = ({ id, event }: EventInteraction) =>
        aggregateContext({
            id,
            event: { ...event, id },
            context,
            listingsType: context.event?.isExpiredEvent
                ? LISTINGS_EXPIRED_INTERFACE
                : LISTINGS_LIVE_INTERFACE,
            subInterface: MINIMAL_ORGANIZER_SUBINTERFACE,
        });
    const { ref } = useTracking<HTMLDivElement>(getTrackingContext);

    return (
        <section
            className="simplified-organizer-info"
            data-testid="simplified-organizer-info"
            aria-label={gettext('Organizer profile').toString()}
            ref={ref}
        >
            <div className="simplified-organizer-info__profile">
                {avatar && (
                    <div className="simplified-organizer-info__avatar">
                        <Avatar imageUrl={avatar} />
                    </div>
                )}
                <div
                    className="simplified-organizer-info__details"
                    data-testid="organizer-info-details"
                >
                    {name && (
                        <span className="simplified-organizer-info__name-by">
                            {/* Translators: Word "By" followed by the organizer's name, ex: By My Cool Organizer Name */}
                            {gettext('By')}{' '}
                            <strong className="simplified-organizer-info__name-link">
                                {name}
                            </strong>
                        </span>
                    )}
                    <OrganizerStats style={style} />
                </div>
            </div>
            <div className="simplified-organizer-info__follow">
                <FollowButton
                    pageArea={
                        pageArea ||
                        LISTING_HEAP_PAGE_AREA_IDS.SIMPLIFIED_ORGANIZER
                    }
                />
            </div>
        </section>
    );
};
